import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Chip, Divider, IconButton, TextField, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    fetchIntegrationSpConfig,
    fetchIntegrationSpMetadataXml,
    setIntegrationProvider,
    testIntegrationSso
} from "../../../../api/admin/integrations/endpoints-referrers";
import type {
    Integration,
    SetProviderRequest,
    SpConfig
} from "../../../../common/interfaces/admin/integration.interface";
import { signInForSsoTest } from "./sso-test-signin";

export interface IntegrationSsoConfigProps {
    companyId: number;
    integration: Integration;
    /** Called with the refreshed integration after a provider/test mutation. */
    onUpdated(updated: Integration): void;
}

/** Pull every PEM certificate block out of a textarea; fall back to one entry. */
function parseCertificates(raw: string): string[] {
    const blocks = raw.match(/-----BEGIN CERTIFICATE-----[\s\S]*?-----END CERTIFICATE-----/g);
    if (blocks && blocks.length > 0) return blocks.map(b => b.trim());
    const trimmed = raw.trim();
    return trimmed ? [trimmed] : [];
}

function CopyField({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="integration-sso__copy-row">
            <TextField
                label={label}
                value={value}
                InputProps={{ readOnly: true }}
                fullWidth
                size="small"
            />
            <Tooltip title={copied ? "Copied" : "Copy"}>
                <IconButton
                    aria-label={`Copy ${label}`}
                    onClick={() => {
                        void navigator.clipboard?.writeText(value);
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 1500);
                    }}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    );
}

export default function IntegrationSsoConfig({
    companyId,
    integration,
    onUpdated
}: IntegrationSsoConfigProps) {
    const providerType = integration.providerType ?? "saml";
    const isSaml = providerType === "saml";

    const [spConfig, setSpConfig] = useState<SpConfig | null>(null);
    const [spError, setSpError] = useState<string | null>(null);

    // IdP details form. Always blank on load — the backend never returns IdP
    // secrets/certs (SR-1), so we can't prefill. Submitting (re)configures.
    const [idpEntityId, setIdpEntityId] = useState("");
    const [ssoURL, setSsoURL] = useState("");
    const [certs, setCerts] = useState("");
    const [clientId, setClientId] = useState("");
    const [issuer, setIssuer] = useState("");
    const [clientSecret, setClientSecret] = useState("");

    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [testError, setTestError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setSpConfig(null);
        setSpError(null);
        fetchIntegrationSpConfig(companyId, integration.id)
            .then(({ data }) => {
                if (!cancelled) setSpConfig(data as SpConfig);
            })
            .catch(err => {
                if (!cancelled)
                    setSpError(err?.response?.data?.message ?? "Couldn't load SP config.");
            });
        return () => {
            cancelled = true;
        };
    }, [companyId, integration.id]);

    const downloadMetadata = useCallback(async () => {
        try {
            const { data } = await fetchIntegrationSpMetadataXml(companyId, integration.id);
            const blob = new Blob([data as string], { type: "application/xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `sp-metadata-${integration.id}.xml`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            setSpError(err?.response?.data?.message ?? "Metadata download failed.");
        }
    }, [companyId, integration.id]);

    const saveProvider = async () => {
        setSaving(true);
        setFormError(null);
        try {
            const body: SetProviderRequest = isSaml
                ? {
                      idpEntityId: idpEntityId.trim(),
                      ssoURL: ssoURL.trim(),
                      x509Certificates: parseCertificates(certs)
                  }
                : {
                      clientId: clientId.trim(),
                      issuer: issuer.trim(),
                      clientSecret: clientSecret.trim()
                  };
            const { data } = await setIntegrationProvider(companyId, integration.id, body);
            onUpdated(data as Integration);
            // Clear the secret-bearing fields once accepted; they're in Firebase now.
            setCerts("");
            setClientSecret("");
        } catch (err: any) {
            setFormError(err?.response?.data?.message ?? "Saving the provider failed.");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        if (!integration.firebaseTenantId || !integration.firebaseProviderId) return;
        // The sign-in runs on the embed host (an authorized Firebase domain),
        // not this per-company admin subdomain. Derive that origin from the
        // integration's own embed URLs (all share the chat.<env> host).
        let chatOrigin: string;
        try {
            chatOrigin = new URL(
                integration.iframeUrl || integration.linkUrl || integration.scriptUrl
            ).origin;
        } catch {
            setTestError("Can't determine the embed host for this integration.");
            return;
        }
        setTesting(true);
        setTestError(null);
        try {
            const idToken = await signInForSsoTest({
                chatOrigin,
                tenantId: integration.firebaseTenantId,
                providerId: integration.firebaseProviderId,
                providerType
            });
            const { data } = await testIntegrationSso(companyId, integration.id, idToken);
            // The endpoint returns the test result; re-fetch the integration so
            // lastSsoTest* (and the activation gate) reflect it. The result also
            // carries outcome/error for immediate display.
            if (data?.outcome === "failed") {
                setTestError(data?.error ?? "The IdP sign-in did not verify against this tenant.");
            }
            onUpdated({
                ...integration,
                lastSsoTestAt: data?.testedAt ?? new Date().toISOString(),
                lastSsoTestOutcome: data?.outcome ?? "failed"
            });
        } catch (err: any) {
            // Firebase popup errors (closed/blocked) or network — surface the code.
            setTestError(
                err?.code ? `Sign-in error: ${err.code}` : (err?.message ?? "Test failed.")
            );
        } finally {
            setTesting(false);
        }
    };

    const providerConfigured = !!integration.providerConfiguredAt;
    const testPassed = integration.lastSsoTestOutcome === "passed";

    return (
        <div className="integration-sso">
            {/* --- 1. SP-side values for the customer's IdP team ------------- */}
            <section className="integration-sso__section">
                <h3>1 · Give these to the IdP team</h3>
                {spError && <Alert severity="error">{spError}</Alert>}
                {spConfig?.type === "saml" && (
                    <>
                        <CopyField label="SP Entity ID" value={spConfig.spEntityId} />
                        <CopyField label="ACS URL (Reply URL)" value={spConfig.acsUrl} />
                        <CopyField label="Name ID format" value={spConfig.nameIdFormat} />
                        <Button onClick={downloadMetadata} size="small">
                            Download SP metadata XML
                        </Button>
                    </>
                )}
                {spConfig?.type === "oidc" && (
                    <CopyField label="Redirect URI" value={spConfig.redirectUri} />
                )}
            </section>

            <Divider />

            {/* --- 2. IdP details (→ Firebase, never stored) ---------------- */}
            <section className="integration-sso__section">
                <h3>2 · Enter the IdP details</h3>
                <Alert severity="info">
                    These are sent to Firebase and never stored by MBAssist. Saving (or changing)
                    them clears any previous test result — you must re-test before activating.
                </Alert>
                {formError && <Alert severity="error">{formError}</Alert>}
                {isSaml ? (
                    <>
                        <TextField
                            label="IdP Entity ID (Issuer)"
                            value={idpEntityId}
                            onChange={e => setIdpEntityId(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="IdP SSO URL (https)"
                            value={ssoURL}
                            onChange={e => setSsoURL(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="X.509 signing certificate(s) — PEM"
                            value={certs}
                            onChange={e => setCerts(e.target.value)}
                            placeholder={
                                "-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"
                            }
                            multiline
                            minRows={4}
                            maxRows={8}
                            fullWidth
                        />
                    </>
                ) : (
                    <>
                        <TextField
                            label="OIDC Client ID"
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="OIDC Issuer URL (https)"
                            value={issuer}
                            onChange={e => setIssuer(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="OIDC Client secret"
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            type="password"
                            fullWidth
                        />
                    </>
                )}
                <Button variant="contained" onClick={saveProvider} disabled={saving}>
                    {saving ? "Saving…" : providerConfigured ? "Update provider" : "Save provider"}
                </Button>
            </section>

            <Divider />

            {/* --- 3. Test & activate (FR-11) ------------------------------- */}
            <section className="integration-sso__section">
                <h3>3 · Test the sign-in</h3>
                {!providerConfigured ? (
                    <Alert severity="info">Save the provider details above first.</Alert>
                ) : (
                    <>
                        <div className="integration-sso__test-status">
                            {integration.lastSsoTestOutcome == null ? (
                                <Chip label="Not tested yet" />
                            ) : testPassed ? (
                                <Chip color="success" label="Last test: passed" />
                            ) : (
                                <Chip color="error" label="Last test: failed" />
                            )}
                            {integration.lastSsoTestAt && (
                                <span className="integration-sso__test-when">
                                    {new Date(integration.lastSsoTestAt).toLocaleString()}
                                </span>
                            )}
                        </div>
                        {testError && <Alert severity="error">{testError}</Alert>}
                        <Button variant="outlined" onClick={runTest} disabled={testing}>
                            {testing ? "Waiting for sign-in…" : "Test SSO sign-in"}
                        </Button>
                        {testPassed ? (
                            <Alert severity="success">
                                This integration passed its test and can be activated (toggle at the
                                top).
                            </Alert>
                        ) : (
                            <Alert severity="warning">
                                A passing test is required before this integration can be activated.
                            </Alert>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}
